import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ShoppingCart, Hotel, UtensilsCrossed, Key, Wrench,
  Home, Grid3X3, BarChart3, Menu, Zap,
  ChevronRight, Wifi, TrendingUp, MessageCircle,
  Package, Clock, Users, Brain, BrainCircuit, Activity, Mic, Star, X,
  Shield, Network, AlertTriangle, CreditCard,
  Scan, Sparkles, BadgeCheck, Globe, Smartphone,
  Layers, Monitor, Headphones, Phone, CheckCircle2, ArrowRight, Building2,
  Handshake, Crown, ArrowUpRight, ExternalLink
} from 'lucide-react';
import { LunaVoiceModel, LunaFace } from './LunaVoiceModel';

interface KangavenaHomeProps {
  onPosLogin: () => void;
  onAdminLogin: () => void;
  onRegister?: () => void;
}

const modules = [
  {
    id: 'retail',
    title: 'Retail & Pharmacy POS',
    subtitle: 'Inventory, Voice Billing & Barcodes',
    icon: ShoppingCart,
    color: 'text-emerald-400',
    border: 'hover:border-emerald-500/50',
    glow: 'group-hover:shadow-[0_0_30px_rgba(16,185,129,0.2)]',
    badge: 'ACTIVE',
  },
  {
    id: 'corporate',
    title: 'Corporate Advisory Suite',
    subtitle: 'Tax, Secretarial, Legal & Voice Filing',
    icon: Building2,
    color: 'text-blue-400',
    border: 'hover:border-blue-500/50',
    glow: 'group-hover:shadow-[0_0_30px_rgba(59,130,246,0.2)]',
    badge: 'NEW',
  },
  {
    id: 'hotel',
    title: 'Hotel & Lodge ERP',
    subtitle: 'Rooms, Bookings & Extras Billing',
    icon: Hotel,
    color: 'text-purple-400',
    border: 'hover:border-purple-500/50',
    glow: 'group-hover:shadow-[0_0_30px_rgba(168,85,247,0.2)]',
    badge: 'ACTIVE',
  },
  {
    id: 'restaurant',
    title: 'Restaurant & Kitchen',
    subtitle: 'Table Layouts, KDS & Split Bills',
    icon: UtensilsCrossed,
    color: 'text-amber-400',
    border: 'hover:border-amber-500/50',
    glow: 'group-hover:shadow-[0_0_30px_rgba(245,158,11,0.2)]',
    badge: 'ACTIVE',
  },
  {
    id: 'autoparts',
    title: 'Auto Parts & Garage',
    subtitle: 'Fitment Lookup & Job Cards',
    icon: Wrench,
    color: 'text-cyan-400',
    border: 'hover:border-cyan-500/50',
    glow: 'group-hover:shadow-[0_0_30px_rgba(6,182,212,0.2)]',
    badge: 'ACTIVE',
  },
];

const allFeatures = [
  { icon: Brain, label: 'Luna AI Voice Billing', sub: 'Speak your orders in Sinhala — Luna bills automatically via voice, WhatsApp, or Telegram', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { icon: Network, label: 'Multi-Store Network', sub: 'Manage branch stores from one central dashboard with real-time multi-location sync', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { icon: Users, label: 'Customer Loyalty & CRM', sub: 'Points program, spend tracking, visit history, birthday reminders — fully integrated', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  { icon: Clock, label: 'Real-Time Stock Sync', sub: 'Every sale updates inventory across all stores instantly to prevent overselling', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { icon: MessageCircle, label: 'WhatsApp & Telegram Orders', sub: 'Customers order via voice notes. Luna processes and generates tax bills automatically', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { icon: CreditCard, label: 'Multi-Method Split Payments', sub: 'Cash, card, mobile transfers, credit ledger — split payments in a single transaction', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  { icon: Shield, label: 'Role-Based RBAC Access', sub: 'Granular permissions for Manager, Cashier, Inventory Lead, and Auditor roles', color: 'text-sky-400', bg: 'bg-sky-500/10' },
  { icon: BarChart3, label: 'Automated P&L Analytics', sub: 'Instant day-end summaries, revenue charts, and profit margins with one tap', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  { icon: Scan, label: 'Fast Barcode Entry', sub: 'Laser-fast checkout with barcode scanner support, custom numpad & hotkeys', color: 'text-teal-400', bg: 'bg-teal-500/10' },
  { icon: Wifi, label: 'Offline-First Engine', sub: 'Keep billing even when internet connection drops. Auto-syncs when online', color: 'text-lime-400', bg: 'bg-lime-500/10' },
  { icon: Globe, label: 'Native Sinhala + English', sub: 'Complete Sinhala UI, voice recognition, and printed receipts tailored for Sri Lanka', color: 'text-rose-400', bg: 'bg-rose-500/10' },
  { icon: Layers, label: 'Hotel Front Desk Operations', sub: 'Room reservations, check-in/out, room service & housekeeping extras billing', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
];

const stats = [
  { key: 'activeStores', label: 'Active Stores', icon: Package, color: 'text-cyan-400' },
  { key: 'totalTransactions', label: 'Total Transactions', icon: TrendingUp, color: 'text-amber-400' },
  { key: 'totalBusinesses', label: 'Businesses', icon: Users, color: 'text-emerald-400' },
  { label: 'Network Uptime', value: '99.9%', icon: Wifi, color: 'text-purple-400' },
];

const navItems = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'modules', label: 'Modules', icon: Grid3X3 },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'menu', label: 'Menu', icon: Menu },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 220, damping: 22 } },
};

export default function KangavenaHome({ onPosLogin, onAdminLogin, onRegister }: KangavenaHomeProps) {
  const [activeNav, setActiveNav] = useState('home');
  const [lunaOpen, setLunaOpen] = useState(false);
  const [lunaRemaining, setLunaRemaining] = useState<number | null>(null);
  const [lunaBlocked, setLunaBlocked] = useState<string | null>(null);
  const [platformStats, setPlatformStats] = useState<{ activeStores: number; totalTransactions: number; totalBusinesses: number } | null>(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [partners, setPartners] = useState<{ official: any[]; connected: any[] }>({ official: [], connected: [] });

  useEffect(() => {
    fetch('/api/platform/stats')
      .then(r => r.json())
      .then(d => setPlatformStats(d))
      .catch(() => {});

    fetch('/api/partners/public')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setPartners({ official: d.official || [], connected: d.connected || [] });
        }
      })
      .catch(() => {});
  }, []);

  const checkLunaAccess = async () => {
    try {
      const checkRes = await fetch('/api/luna/public/check');
      const checkData = await checkRes.json();
      if (!checkData.allowed) {
        setLunaBlocked('VPN or proxy detected. Please use your direct network connection.');
        return false;
      }
      const statusRes = await fetch('/api/luna/public/status');
      const statusData = await statusRes.json();
      if (!statusData.allowed) {
        setLunaBlocked(`Usage limit reached. Please try again in ${statusData.windowResetMinutes} minutes.`);
        return false;
      }
      setLunaRemaining(statusData.remainingMinutes);
      return true;
    } catch {
      return true;
    }
  };

  const displayedFeatures = showAllFeatures ? allFeatures : allFeatures.slice(0, 8);

  return (
    <div className="min-h-screen w-full bg-[#05070B] text-white font-sans flex flex-col relative overflow-x-hidden">

      {/* Deep Space Glowing Orbs Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute -top-40 right-0 w-[650px] h-[650px] rounded-full bg-emerald-500/10 blur-[160px]" />
        <div className="absolute top-1/3 -left-40 w-[600px] h-[600px] rounded-full bg-purple-500/10 blur-[160px]" />
        <div className="absolute bottom-10 left-1/3 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[150px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
      </div>

      {/* ── Top Navigation Bar ────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-2xl border-b border-white/10 px-4 sm:px-8 py-3.5 flex items-center justify-between shrink-0 w-full shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-400 p-0.5 shadow-[0_0_15px_rgba(16,185,129,0.4)]">
            <div className="w-full h-full bg-slate-950 rounded-[10px] flex items-center justify-center">
              <BrainCircuit className="w-5 h-5 text-emerald-400 animate-pulse" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-black text-lg tracking-tight font-mono">KANGAVENA</span>
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">AI CLOUD</span>
            </div>
            <p className="text-[9px] text-slate-400 font-mono tracking-widest uppercase">Autonomous Commerce Platform · Sri Lanka</p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => onRegister ? onRegister() : onPosLogin()}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all hover:scale-[1.02] active:scale-95 cursor-pointer"
          >
            <Sparkles size={14} className="text-amber-300" />
            <span>100K FREE TRIAL</span>
          </button>
          <button
            onClick={onPosLogin}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider bg-slate-900 border border-slate-800 hover:border-cyan-500/50 text-slate-300 hover:text-white transition-all hover:scale-[1.02] active:scale-95 cursor-pointer"
          >
            <Shield size={14} />
            <span>LOGIN</span>
          </button>
        </div>
      </header>

      {/* ── Scrollable Body ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden pb-28 scroll-smooth">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 flex flex-col gap-12"
        >

          {/* ── Hero Section ──────────────────────────────────────── */}
          <motion.div variants={itemVariants} className="text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8 pt-2">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono font-bold mb-4 shadow-[0_0_15px_rgba(16,185,129,0.15)]">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                AUTONOMOUS COMMERCE & ERP NETWORK · SRI LANKA
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.1] tracking-tight font-sans">
                The Next-Gen AI Operating System for{' '}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">
                  Sri Lankan Commerce.
                </span>
              </h1>
              <p className="text-sm sm:text-base text-slate-300 mt-4 leading-relaxed max-w-xl font-normal">
                Retail POS, Hotel ERP, Restaurant KDS & Auto Parts Management — unified under one autonomous AI brain. Sinhala voice billing, WhatsApp orders, and real-time inventory sync.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3.5 mt-6">
                <button
                  type="button"
                  onClick={() => onRegister ? onRegister() : onPosLogin()}
                  className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl text-xs font-black uppercase tracking-wider text-white shadow-[0_0_30px_rgba(6,182,212,0.35)] bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-500 hover:from-cyan-500 hover:to-emerald-400 transition-all hover:scale-[1.02] active:scale-95 cursor-pointer"
                >
                  <Sparkles size={16} className="text-amber-300" />
                  <span>Start Free Trial (100k Tokens)</span>
                </button>
                <a
                  href="https://wa.me/94767114888?text=Hi%20Kangavena%2C%20I%20want%20to%20register%20my%20store"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl text-xs font-black uppercase tracking-wider text-slate-200 hover:text-white bg-slate-900/90 border border-emerald-500/30 hover:border-emerald-400 shadow-[0_0_20px_rgba(37,211,102,0.15)] transition-all hover:scale-[1.02] active:scale-95"
                >
                  <MessageCircle size={16} className="text-emerald-400" />
                  WhatsApp Us
                </a>
                <button
                  onClick={() => setLunaOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl text-xs font-black uppercase tracking-wider bg-slate-900/90 border border-purple-500/30 text-purple-300 hover:bg-purple-500/10 hover:border-purple-500/50 shadow-[0_0_20px_rgba(168,85,247,0.15)] transition-all hover:scale-[1.02] active:scale-95"
                >
                  <Sparkles size={16} className="text-purple-400" />
                  Luna AI Demo
                </button>
              </div>
            </div>

            {/* Hero AI Interactive Badge Box */}
            <div className="w-full max-w-sm bg-slate-900/70 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/10 blur-3xl pointer-events-none" />
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                    <LunaFace sentiment="neutral" isListening={false} className="w-7 h-7 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-black text-sm text-white font-mono">LUNA NEURAL BRAIN</h3>
                    <span className="text-[10px] text-emerald-400 font-mono font-bold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Sinhala NLP Active
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                <div className="bg-slate-950/80 border border-white/10 rounded-xl p-2.5 text-xs font-mono text-slate-300">
                  <span className="text-purple-400 font-bold">Voice:</span> "අද මුළු sales කීයද?"
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-2.5 text-xs font-mono text-emerald-300">
                  <span className="font-bold">Luna:</span> "අද මුළු sales රු. 45,320.00 — ✅ 12% Growth"
                </div>
              </div>
              <button
                onClick={() => setLunaOpen(true)}
                className="w-full py-2.5 rounded-xl bg-purple-600/20 border border-purple-500/40 text-purple-300 font-black text-xs uppercase tracking-wider hover:bg-purple-600/30 transition-all flex items-center justify-center gap-2"
              >
                <Mic size={14} /> Try Voice Command
              </button>
            </div>
          </motion.div>

          {/* ── Business Verticals Modules ───────────────────────────── */}
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-white font-mono tracking-tight flex items-center gap-2">
                  <Grid3X3 className="text-emerald-400" size={22} /> Unified Industry Verticals
                </h2>
                <p className="text-xs text-slate-400 mt-1">Tailored workflows engineered specifically for Sri Lankan enterprise operations</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {modules.map((mod) => {
                const Icon = mod.icon;
                return (
                  <motion.div
                    key={mod.id}
                    variants={itemVariants}
                    whileHover={{ y: -4 }}
                    className={`bg-slate-900/60 backdrop-blur-xl border border-white/10 ${mod.border} ${mod.glow} rounded-3xl p-6 transition-all duration-300 cursor-pointer group relative overflow-hidden flex flex-col justify-between`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <Icon size={24} className={mod.color} />
                        </div>
                        <span className="px-2.5 py-1 rounded-full text-[9px] font-black font-mono tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          {mod.badge}
                        </span>
                      </div>
                      <h3 className="text-white font-black text-base tracking-tight mb-1">{mod.title}</h3>
                      <p className="text-slate-400 text-xs leading-relaxed">{mod.subtitle}</p>
                    </div>

                    <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-xs font-bold text-slate-300 group-hover:text-emerald-400 transition-colors">
                      <span>Explore Capabilities</span>
                      <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* ── Network Statistics ────────────────────────────────────── */}
          <motion.div variants={itemVariants}>
            <div className="bg-slate-950/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {stats.map((stat) => {
                  const Icon = stat.icon;
                  const val = stat.key !== undefined && platformStats
                    ? String(platformStats[stat.key as keyof typeof platformStats])
                    : stat.value || '—';
                  return (
                    <div key={stat.label} className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                        <Icon size={22} className={stat.color} />
                      </div>
                      <div>
                        <p className="text-white font-black text-2xl font-mono leading-none">
                          {val}{stat.key ? '+' : ''}
                        </p>
                        <p className="text-slate-400 text-xs font-medium mt-1">{stat.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* ── All Features Showcase Grid ───────────────────────────── */}
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-white font-mono tracking-tight flex items-center gap-2">
                  <Sparkles className="text-purple-400" size={22} /> Platform Capabilities
                </h2>
                <p className="text-xs text-slate-400 mt-1">Enterprise infrastructure tailored for seamless business automation</p>
              </div>
              <button
                onClick={() => setShowAllFeatures(!showAllFeatures)}
                className="text-xs font-black text-purple-400 hover:text-purple-300 flex items-center gap-1 uppercase tracking-wider font-mono transition-colors"
              >
                {showAllFeatures ? 'Collapse List' : `View All ${allFeatures.length} Features`}
                <ChevronRight size={14} className={showAllFeatures ? 'rotate-90' : ''} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {displayedFeatures.map((feat, idx) => {
                const Icon = feat.icon;
                return (
                  <motion.div
                    key={feat.label}
                    variants={itemVariants}
                    className="bg-slate-900/60 backdrop-blur-xl border border-white/10 hover:border-purple-500/40 rounded-2xl p-5 transition-all duration-300 group"
                  >
                    <div className={`w-10 h-10 rounded-xl ${feat.bg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon size={20} className={feat.color} />
                    </div>
                    <h3 className="text-white font-extrabold text-sm mb-1">{feat.label}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed">{feat.sub}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* ── Strategic Alliances & Ecosystem Partners Showcase ───────── */}
          <motion.div variants={itemVariants} className="space-y-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-mono font-bold mb-3 shadow-[0_0_15px_rgba(245,158,11,0.15)]">
                  <Handshake size={14} className="text-amber-400" />
                  STRATEGIC ALLIANCES & BUSINESS ECOSYSTEM
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-white font-mono tracking-tight flex items-center gap-2.5">
                  Empowering Commerce With Verified Partners
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-2xl font-normal leading-relaxed">
                  Kangavena partners with premier technology leaders, certified enterprise integrators, and connected ecosystem innovators across Sri Lanka.
                </p>
              </div>

              <a
                href="https://wa.me/94767114888?text=Hi%20Kangavena%2C%20I%20want%20to%20become%20a%20partner"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 border border-white/10 hover:border-amber-500/50 text-xs font-mono font-bold text-slate-300 hover:text-white transition-all shrink-0 hover:scale-[1.02] active:scale-95"
              >
                <span>Partner With Us</span>
                <ArrowUpRight size={14} className="text-amber-400" />
              </a>
            </div>

            {/* Subsection 1: Official Business Partners (Premium Alliance) */}
            <div className="space-y-4">
              <div className="flex items-center gap-2.5 pb-2 border-b border-white/5">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <h3 className="text-sm sm:text-base font-black text-amber-300 font-mono uppercase tracking-wider flex items-center gap-2">
                  <Crown size={16} className="text-amber-400" /> Official Business Partners
                </h3>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 font-mono">
                  PREMIUM ALLIANCE
                </span>
              </div>

              {partners.official.length === 0 ? (
                <div className="bg-gradient-to-br from-amber-950/20 via-slate-950 to-slate-900/60 border border-amber-500/30 rounded-3xl p-6 sm:p-8 relative overflow-hidden shadow-xl">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="max-w-xl text-center sm:text-left">
                      <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-black font-mono bg-amber-500/20 text-amber-300 border border-amber-500/40 mb-2">
                        OFFICIAL PARTNER NETWORK EXPANSION
                      </span>
                      <h4 className="text-white font-extrabold text-lg font-sans">
                        Become a Kangavena Official Business Partner
                      </h4>
                      <p className="text-slate-400 text-xs mt-2 leading-relaxed">
                        Join Sri Lanka's leading autonomous AI commerce infrastructure. Official partners receive certified verification, co-marketing, and deep system integrations.
                      </p>
                    </div>
                    <a
                      href="https://wa.me/94767114888?text=Hi%20Kangavena%2C%20I%20want%20to%20apply%20as%20an%20Official%20Business%20Partner"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-xs font-mono uppercase tracking-wider shadow-[0_0_25px_rgba(245,158,11,0.3)] transition-all hover:scale-[1.02] shrink-0"
                    >
                      <Crown size={14} /> Apply For Partnership
                    </a>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {partners.official.map((partner) => (
                    <div
                      key={partner.id}
                      className="bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-amber-500/40 hover:border-amber-400/80 rounded-3xl p-6 shadow-[0_0_30px_rgba(245,158,11,0.12)] hover:shadow-[0_0_45px_rgba(245,158,11,0.25)] transition-all duration-300 group relative overflow-hidden flex flex-col justify-between"
                    >
                      <div className="absolute top-0 right-0 w-36 h-36 bg-amber-500/10 blur-3xl pointer-events-none" />

                      <div>
                        {/* Logo & Category Badge */}
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div className="w-16 h-16 rounded-2xl bg-slate-950 border border-amber-500/30 p-2.5 flex items-center justify-center shrink-0 overflow-hidden shadow-inner group-hover:scale-105 group-hover:border-amber-400 transition-all duration-300">
                            {partner.logoUrl ? (
                              <img
                                src={partner.logoUrl}
                                alt={partner.name}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  (e.currentTarget as HTMLElement).style.display = 'none';
                                  const fb = e.currentTarget.parentElement?.querySelector('.official-fallback');
                                  if (fb) (fb as HTMLElement).style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`official-fallback w-full h-full rounded-xl flex items-center justify-center font-mono font-black text-base bg-amber-500/20 text-amber-300 ${
                                partner.logoUrl ? 'hidden' : 'flex'
                              }`}
                            >
                              {partner.name.slice(0, 2).toUpperCase()}
                            </div>
                          </div>

                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black font-mono tracking-wider bg-gradient-to-r from-amber-500/20 to-amber-600/20 text-amber-300 border border-amber-500/40 shadow-[0_0_15px_rgba(245,158,11,0.2)]">
                            <Crown size={12} className="text-amber-400" />
                            OFFICIAL
                          </span>
                        </div>

                        {/* Name & Bio */}
                        <h4 className="text-white font-black text-lg tracking-tight font-sans flex items-center gap-2 group-hover:text-amber-300 transition-colors">
                          {partner.name}
                          <BadgeCheck size={18} className="text-amber-400 shrink-0" />
                        </h4>

                        <p className="text-slate-300 text-xs leading-relaxed mt-2.5 font-normal">
                          {partner.description || 'Verified enterprise business partner connected with Kangavena AI Cloud.'}
                        </p>
                      </div>

                      {/* Direct Website Link Button */}
                      {partner.websiteUrl && (
                        <div className="mt-6 pt-4 border-t border-amber-500/20 flex items-center justify-between">
                          <a
                            href={partner.websiteUrl.startsWith('http') ? partner.websiteUrl : `https://${partner.websiteUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-mono font-bold text-amber-400 hover:text-amber-300 group/link transition-colors"
                          >
                            <span>Visit Official Website</span>
                            <ArrowUpRight size={14} className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Subsection 2: Connected Partners (Ecosystem Network) */}
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-2.5 pb-2 border-b border-white/5">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <h3 className="text-sm sm:text-base font-black text-cyan-300 font-mono uppercase tracking-wider flex items-center gap-2">
                  <Zap size={16} className="text-cyan-400" /> Connected Partners
                </h3>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-mono">
                  ECOSYSTEM NETWORK
                </span>
              </div>

              {partners.connected.length === 0 ? (
                <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h5 className="text-white font-bold text-sm">Kangavena Connected Partner Network</h5>
                    <p className="text-slate-400 text-xs mt-1">
                      Integrate your services, hardware, or delivery systems with Kangavena AI Cloud.
                    </p>
                  </div>
                  <a
                    href="https://wa.me/94767114888?text=Hi%20Kangavena%2C%20I%20want%20to%20connect%20my%20business%20as%20a%20partner"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 font-mono font-bold text-xs hover:bg-cyan-500/30 transition-all shrink-0"
                  >
                    Connect Your Business
                  </a>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {partners.connected.map((partner) => (
                    <div
                      key={partner.id}
                      className="bg-slate-900/60 backdrop-blur-xl border border-white/10 hover:border-cyan-500/50 rounded-2xl p-5 transition-all duration-300 group flex flex-col justify-between hover:shadow-[0_0_25px_rgba(6,182,212,0.15)]"
                    >
                      <div>
                        {/* Logo & Category */}
                        <div className="flex items-center justify-between mb-3.5">
                          <div className="w-12 h-12 rounded-xl bg-slate-950 border border-white/10 p-2 flex items-center justify-center shrink-0 overflow-hidden shadow-inner group-hover:scale-105 transition-transform">
                            {partner.logoUrl ? (
                              <img
                                src={partner.logoUrl}
                                alt={partner.name}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  (e.currentTarget as HTMLElement).style.display = 'none';
                                  const fb = e.currentTarget.parentElement?.querySelector('.connected-fallback');
                                  if (fb) (fb as HTMLElement).style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`connected-fallback w-full h-full rounded-lg flex items-center justify-center font-mono font-black text-xs bg-cyan-500/20 text-cyan-300 ${
                                partner.logoUrl ? 'hidden' : 'flex'
                              }`}
                            >
                              {partner.name.slice(0, 2).toUpperCase()}
                            </div>
                          </div>

                          <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 font-mono">
                            CONNECTED
                          </span>
                        </div>

                        <h5 className="text-white font-extrabold text-sm font-sans mb-1 group-hover:text-cyan-300 transition-colors">
                          {partner.name}
                        </h5>
                        <p className="text-slate-400 text-xs leading-relaxed line-clamp-2">
                          {partner.description || 'Connected ecosystem partner.'}
                        </p>
                      </div>

                      {partner.websiteUrl && (
                        <div className="mt-4 pt-3 border-t border-white/5">
                          <a
                            href={partner.websiteUrl.startsWith('http') ? partner.websiteUrl : `https://${partner.websiteUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-mono text-cyan-400 hover:text-cyan-300 transition-colors"
                          >
                            <span>Visit Website</span>
                            <ArrowUpRight size={12} />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* ── Security & Architecture Guarantees ─────────────────────── */}
          <motion.div variants={itemVariants}>
            <div className="bg-slate-950/80 backdrop-blur-2xl border border-emerald-500/30 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 blur-[100px] pointer-events-none" />

              <div className="text-center max-w-xl mx-auto mb-8">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                  <Shield size={28} className="text-emerald-400" />
                </div>
                <h2 className="text-2xl font-black text-white font-mono tracking-tight">Bank-Grade Zero-Trust Architecture</h2>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                  Strict tenant isolation, end-to-end TLS encryption, server-side financial audit validation, and 24/7 security monitoring.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { title: 'Server-Validated Financials', desc: 'Transaction sums are strictly verified server-side before database write.', icon: Activity, color: 'text-amber-400' },
                  { title: 'Isolated Tenant Schemas', desc: 'No cross-tenant data leaks are structurally possible at the ORM layer.', icon: Network, color: 'text-emerald-400' },
                  { title: 'Strict RBAC Governance', desc: 'Role-based access locks functions according to employee responsibilities.', icon: Key, color: 'text-cyan-400' },
                ].map((sec, i) => {
                  const Icon = sec.icon;
                  return (
                    <div key={i} className="bg-slate-900/80 border border-white/10 rounded-2xl p-5 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                        <Icon size={20} className={sec.color} />
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-xs mb-1">{sec.title}</h4>
                        <p className="text-slate-400 text-[11px] leading-relaxed">{sec.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* ── Footer ────────────────────────────────────────────────── */}
          <footer className="pt-8 pb-6 border-t border-white/10 flex flex-col items-center gap-4 text-center">
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs font-mono text-slate-400">
              <a href="https://wa.me/94767114888" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors flex items-center gap-1.5">
                <Phone size={14} className="text-emerald-400" /> WhatsApp Hotline: 076 711 4888
              </a>
              <span>•</span>
              <a href="/privacy" className="hover:text-slate-200 transition-colors">Privacy Policy</a>
              <span>•</span>
              <a href="/terms" className="hover:text-slate-200 transition-colors">Terms of Service</a>
              <span>•</span>
              <a href="/returns" className="hover:text-slate-200 transition-colors">Return Policy</a>
            </div>
            <p className="text-xs text-slate-400 font-mono">
              © {new Date().getFullYear()} <span className="text-white font-bold">Kangavena AI Cloud</span> · Developed by <span className="text-emerald-400 font-bold">Kangavena</span> · Sri Lanka
            </p>
          </footer>

        </motion.div>
      </div>

      {/* ── Persistent Floating Luna AI Launcher FAB ──────────────────────── */}
      <AnimatePresence>
        {!lunaOpen && (
          <motion.button
            key="luna-fab"
            initial={{ opacity: 0, scale: 0.7, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 20 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.92 }}
            onClick={async () => { setLunaOpen(true); await checkLunaAccess(); }}
            className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.4)] border border-emerald-400/40 bg-gradient-to-tr from-emerald-600 via-teal-600 to-emerald-400"
            title="Talk to Luna AI"
          >
            <LunaFace sentiment="neutral" isListening={false} className="w-8 h-8 text-white" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Luna Full-Screen Overlay ─────────────────────────────── */}
      <AnimatePresence>
        {lunaOpen && (
          <motion.div
            key="luna-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-[#05070B]"
          >
            <button
              onClick={() => { setLunaOpen(false); setLunaBlocked(null); }}
              className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold transition-all"
            >
              <X size={14} /> Close Demo
            </button>

            {lunaBlocked ? (
              <div className="flex flex-col items-center justify-center h-full px-6 text-center">
                <div className="w-20 h-20 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
                  <Shield size={36} className="text-red-400" />
                </div>
                <h2 className="text-white font-black text-xl mb-2 font-mono">Access Restricted</h2>
                <p className="text-red-300/80 text-xs max-w-sm mb-6 font-mono">{lunaBlocked}</p>
                <button
                  onClick={() => { setLunaOpen(false); setLunaBlocked(null); }}
                  className="px-6 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold transition-all uppercase tracking-wider font-mono"
                >
                  Close
                </button>
              </div>
            ) : (
              <LunaVoiceModel
                apiKey=""
                role="public"
                onDisconnect={() => { setLunaOpen(false); setLunaBlocked(null); }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
